import { z } from "zod";
import { DateInputField } from "@/components/shared/form";
import { TenderType } from "@/graphql/__generated__/graphql";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { UseFormReturn } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import RenderFormRowComponent from "./RenderFormRowComponent";

const createDateSchema = (t: TFunction) => {
  return z.date({
    error: (issue) => issue.input === undefined ?
      t("schemas:createtender.deadline.daterequired")
      :
      t("schemas:createtender.deadline.dateinvalid"),
  }).refine((date) => date instanceof Date && !isNaN(date.getTime()), {
    message: t("schemas:createtender.deadline.dateinvalid"),
  });
};

/**
 * Creates a zod schema for the deadline form group
 * All fields are requirements are based off quickoffer tender type, public and private tenders validation handled in main schema
 * @param t - The translation function
 * @returns The zod schema
 */
export const createDeadlineSchema = (t: TFunction) => {
  return z.object({
    deadline: z.object({
      publication: createDateSchema(t).optional(),
      submission: createDateSchema(t),
      questions: createDateSchema(t).optional(),
      outsideInformation: createDateSchema(t).optional(),
      siteVisit: z.date({
        error: (issue) => issue.input === undefined ?
          t("schemas:createtender.deadline.daterequired")
          :
          t("schemas:createtender.deadline.dateinvalid"),
      }).refine((date) => date instanceof Date && !isNaN(date.getTime()), {
        message: t("schemas:createtender.deadline.dateinvalid"),
      }).optional(),
    }),
  });
};

/**
 * DeadlineFormGroup component for editing the deadline form group
 * Utilises newtender locales as these values are based off the new tender form, if newtender changes, these will change with it.
 * @param form - The form object
 * @returns The rendered deadline form group component
 */
export function DeadlineFormGroup({ form }: { form: UseFormReturn<any> }) {
  const { t } = useTranslation();

  const formFields = [
    {
      name: "deadline.publication",
      label: t("newtender.formgroups.deadline.inputs.publication.label"),
      allowOlderDates: true,
      hideOnPrivate: true
    },
    {
      name: "deadline.submission",
      label: t("newtender.formgroups.deadline.inputs.submission.label"),
      allowOlderDates: true,
      hideOnPrivate: false
    },
    {
      name: "deadline.questions",
      label: t("newtender.formgroups.deadline.inputs.questions.label"),
      hideOnPrivate: true,
      allowOlderDates: true
    },
    {
      name: "deadline.outsideInformation",
      label: t("newtender.formgroups.deadline.inputs.outsideinformation.label"),
      hideOnPrivate: false,
      showOptional: true,
      allowOlderDates: true
    },
    {
      name: "deadline.siteVisit",
      label: t("newtender.formgroups.deadline.inputs.sitevisit.label"),
      hideOnPrivate: true,
      showOptional: true,
      allowOlderDates: true
    },
  ];

  const tenderType = form.watch("description.type");

  return (
    <Card id="deadline">
      <CardHeader className="pt-4 pb-2 select-none">
        <CardTitle className="text-lg">{t("newtender.formgroups.deadline.title")}</CardTitle>
      </CardHeader>
      <CardContent className="p-2">
        {
          formFields.map((field) => (
            (field.hideOnPrivate && tenderType === TenderType.Private) ? null : (
              <RenderFormRowComponent key={field.name} label={field.label}>
                <DateInputField
                  form={form}
                  name={field.name}
                  allowOlderDates={field.allowOlderDates}
                  showOptional={field.showOptional}
                  className="w-full"
                />
              </RenderFormRowComponent>
            )
          ))
        }
      </CardContent>
    </Card>
  );
}
